/**
 * SharedPipeline — the kanban pipeline component used by all three role
 * dashboards. ALL THREE ROLES SEE ONE IDENTICAL BOARD (CLAUDE.md §30): the
 * same six columns and the same card set — client file folders, preparation
 * requests, review calendar cards and referral cards, test records included.
 * Only the TOP section of each dashboard (greeting, quote, intro copy, month
 * picker, stat row) differs by role, and that lives in the dashboards, not
 * here.
 *
 * ONE EXCEPTION — the Inbox column (stage key 'submitted'): it shows only
 * the card types the ACTIVE role acts on (Adviser: all four; Administration
 * Assistant: file folders only; New Business Assistant: preparation requests
 * only). The rule lives in ONE pure function, filterInboxByRole in
 * src/utils/inboxColumnFilter.js, applied to the submitted column only,
 * before its header count and empty state. Every other column still shows
 * every card type to every role, and no fetch or access rule changes.
 *
 * Only who may OPEN a card differs by active role:
 *
 *   Adviser                  → opens file folders + prep notebook cards
 *                              + review calendar cards + referral cards
 *   Administration Assistant → opens file folders, plus the prep
 *                              notebook cards HE loaded himself (§26.1)
 *   New Business Assistant   → opens prep notebook cards
 *
 * Cards a role may not open still render in full; they lose the openable
 * affordance (`interactive={false}`) and their click is a no-op — the same
 * treatment review calendar cards already use for non-Advisers. The
 * open-permission checks on the detail views (FileDetail /
 * PrepRequestDetail) remain the backstop for deep links and direct URLs.
 *
 * STATS SEPARATION (CLAUDE.md §10 / §21.1 / §25.6): this component assembles
 * record types for DISPLAY only. It performs NO counting that feeds any
 * file statistic. File stat cards and the bonus counter are computed elsewhere
 * (dashboardStats.js) over files exclusively and are never passed prep rows.
 * Showing prep sheets on a board therefore cannot move a file statistic —
 * visibility here and the stats contract are entirely separate paths.
 *
 * The column list is owned by this component (PIPELINE_STAGES below) rather
 * than passed in, so no dashboard can define a board of its own.
 */

import React, { useEffect } from 'react';
import { Inbox, Clock, Pause, Send, CheckCircle2, Trophy } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import FolderCard from '../files/FolderCard';
import FannedStack from '../files/FannedStack';
import PrepSheetCard from '../files/PrepSheetCard';
import PrepFannedStack from '../files/PrepFannedStack';
import CalendarCard from '../files/CalendarCard';
import ReferralCard from '../files/ReferralCard';
import InProgressEmptyWarning from '../files/InProgressEmptyWarning';
import { shouldShowNoActiveBanner } from '../../utils/pipelineInvariants';
import { isInSelectedMonth } from '../../utils/dashboardStats';
import { getPrepsForColumn, canOpenPrepRequest, PREP_PIPELINE_COLUMNS } from '../../utils/prepRequests';
import { getReviewCardsForColumn } from '../../utils/reviewPlanner';
import { getReferralsForColumn, REFERRAL_PIPELINE_COLUMNS } from '../../utils/referrals';
import { filterInboxByRole } from '../../utils/inboxColumnFilter';
import { isSealedInInbox } from '../../utils/inboxDrawGate';
import { triggerBackstopSweep } from '../../api/purgeAttachmentsApi';

// Attachment-purge backstop (CLAUDE.md §29): catches any file whose
// event-driven purge failed (or that was already past Awaiting Signature
// at deploy time). Module-level flag debounces it to AT MOST ONCE per app
// load — the pipeline component mounts on every dashboard, and we don't
// want a sweep per re-mount. Fire-and-forget; failures never affect load.
let backstopSweepFired = false;

// The six pipeline columns — ONE definition for every role's board.
// `key` is the INTERNAL status value stored in the database — never change it.
// `label` is the DISPLAY label only: the first column reads "Inbox" while its
// stored status value stays 'submitted' (CLAUDE.md §7.1).
export const PIPELINE_STAGES = [
  { key: 'submitted', label: 'Inbox', color: '#1B9AAA', icon: Inbox },
  { key: 'in_progress', label: 'In Progress', color: '#2EC4B6', icon: Clock },
  { key: 'paused', label: 'Paused', color: '#E76F51', icon: Pause },
  { key: 'sent_to_sign', label: 'Awaiting Signature', color: '#F4A261', icon: Send },
  { key: 'signed', label: 'Signed & Sent to New Business', color: '#4BA3C3', icon: CheckCircle2 },
  { key: 'completed', label: 'File Completed', color: '#1A2B3C', icon: Trophy },
];

export default function SharedPipeline({
  files = [],
  prepRequests = [],
  reviewClients = [],
  reviewGroupLabels = {},
  referrals = [],
  noteCounts = {},
  pendingNoteCountsByRole = {},
  prepNoteCounts = {},
  prepPendingNoteCountsByRole = {},
  flaggedCounts = {},
  filterMonth,
  onFileClick,
  onPrepClick,
  onReviewClick,
  onReferralClick,
}) {
  // VISIBILITY vs ACCESS (CLAUDE.md §30) — two separate things:
  //
  //   Visibility: every role sees the same board. All three role dashboards
  //   render the identical pipeline — same columns, same cards, files AND
  //   preparation requests AND review calendar cards, test records included —
  //   so the team shares one picture of the work. The single exception is
  //   the Inbox column, which each role sees narrowed to the card types it
  //   acts on (filterInboxByRole, applied below to 'submitted' only).
  //
  //   Access: who may OPEN and work a card is gated by the CURRENTLY ACTIVE
  //   role (not the user account), so switching roles changes access
  //   immediately. A role that may not open a card type still sees those
  //   cards in full — they simply lose the openable affordance
  //   (`interactive={false}`, the same treatment review calendar cards use
  //   for non-Advisers) and clicking them does nothing. This is app-level
  //   workflow gating only; RLS is untouched, and the deep-link guards on
  //   FileDetail / PrepRequestDetail remain the backstop for direct URLs.
  const { role, user } = useAuth();
  const canOpenFiles = role === 'adviser' || role === 'assistant';
  // Prep-request access is PER RECORD (§26.1): the Adviser and the New
  // Business Assistant may open any request, while the Administration
  // Assistant may open only the ones he loaded himself. Cards he may not
  // open still render in full, inert, exactly as before.
  const canOpenPrep = (prep) => canOpenPrepRequest(prep, role, user?.email);
  // Review calendar cards (CLAUDE.md §34.9): like every other card kind,
  // ALL roles see them, but only the Adviser can open one — non-Adviser
  // roles get the same `interactive={false}` non-openable treatment the
  // other card types use for cross-role display. Calendar cards join no
  // counter, stat, SLA, or concurrency computation — they are a
  // display-only view of review_clients rows.
  const canOpenReviews = role === 'adviser';
  // Referral cards (CLAUDE.md referral section): the fourth card type.
  // ALL roles see them; only the Adviser may open one — the same
  // `interactive={false}` treatment as calendar cards. Referral cards join
  // no file / prep / review statistic, SLA or concurrency computation.
  const canOpenReferrals = role === 'adviser';

  // Owner-only illumination (CLAUDE.md §31.4): scope the role-keyed pending
  // maps down to THIS viewer's active role. A card lights only when the
  // active role itself has unacknowledged notes on it — the author and third
  // parties see the card completely normal. Because `role` comes from
  // useAuth(), a role switch re-renders and re-scopes automatically, so the
  // same board can light differently per role.
  const myPending = (fileId) => (pendingNoteCountsByRole[fileId] || {})[role] || 0;
  const myPrepPending = (prepId) => (prepPendingNoteCountsByRole[prepId] || {})[role] || 0;

  // Opening is gated by the active role; a card type this role may not work
  // is rendered inert and its click is a no-op (see VISIBILITY vs ACCESS).
  const handleFileOpen = (file) => canOpenFiles && onFileClick && onFileClick(file);
  const handlePrepOpen = (prep) => canOpenPrep(prep) && onPrepClick && onPrepClick(prep);

  // Backstop attachment-purge sweep — once per app load (see flag above).
  useEffect(() => {
    if (backstopSweepFired) return;
    backstopSweepFired = true;
    triggerBackstopSweep();
  }, []);

  // Pipeline visibility rule (CLAUDE.md §9.4 + §21): active files always
  // visible; completed files only when completed_at is in the selected month.
  // Test records (CLAUDE.md §33) always render like real records — hard
  // deletion is their sole cleanup mechanism.
  const pipelineFiles = files.filter((f) => {
    if (f.status !== 'completed') return true;
    if (!f.completedAt) {
      console.warn('[Inbox] Completed file missing completedAt — showing as fail-safe:', f.id);
      return true;
    }
    return isInSelectedMonth(f.completedAt, filterMonth);
  });

  const getStageFiles = (status) => {
    const result = pipelineFiles.filter((f) => f.status === status);
    if (status === 'submitted') {
      result.sort((a, b) => {
        const aUrgent = a.priority === 'Urgent' ? 0 : 1;
        const bUrgent = b.priority === 'Urgent' ? 0 : 1;
        if (aUrgent !== bUrgent) return aUrgent - bUrgent;
        return new Date(a.submittedAt) - new Date(b.submittedAt);
      });
    }
    if (status === 'completed') {
      result.sort((a, b) => {
        const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        if (bTime !== aTime) return bTime - aTime;
        return (b.id || '').localeCompare(a.id || '');
      });
    }
    return result;
  };

  return (
    <div className="pipeline-scroll" style={styles.pipeline}>
      {PIPELINE_STAGES.map((stage) => {
        // Files are assembled for every role's board — visibility is shared,
        // only the openable affordance differs (see VISIBILITY vs ACCESS).
        const stageFilesAll = getStageFiles(stage.key);
        // Prep sheets are assembled for EVERY role's board — no role filter.
        // The column list is the only thing that limits them: prep sheets
        // live in Inbox / In Progress / Completed only (§27.2).
        const stagePrepsAll = PREP_PIPELINE_COLUMNS.includes(stage.key)
          ? getPrepsForColumn(prepRequests, stage.key, filterMonth)
          : [];
        // Calendar cards — all roles, one card per UNIT (a group renders
        // a single card, §34.10). Column mapping lives in reviewPlanner.js
        // (submitted / appointment_made only; completed reviews simply
        // stop matching the filter and leave the board).
        const stageReviewCardsAll = getReviewCardsForColumn(reviewClients, stage.key, reviewGroupLabels);
        // Referral cards — all roles, no role filter. The column map is the
        // only limit: Inbox / In Progress / Paused / Completed, never
        // Awaiting Signature or Signed & Sent to New Business
        // (REFERRAL_PIPELINE_COLUMNS in utils/referrals.js).
        const stageReferralsAll = Object.values(REFERRAL_PIPELINE_COLUMNS).includes(stage.key)
          ? getReferralsForColumn(referrals, stage.key, filterMonth)
          : [];
        // INBOX ROLE FILTER — the one place visibility depends on role.
        // For the 'submitted' column only, narrow the four assembled
        // arrays to the card types the ACTIVE role acts on (Adviser: all;
        // Administration Assistant: files; New Business Assistant: preps).
        // Every other column passes through untouched. This runs BEFORE
        // stageCardCount so the header badge and empty state match what
        // is rendered. Access flags (canOpen*) are not involved.
        const {
          files: stageFiles,
          preps: stagePreps,
          reviews: stageReviewCards,
          referrals: stageReferrals,
        } = stage.key === 'submitted'
          ? filterInboxByRole(role, {
              files: stageFilesAll,
              preps: stagePrepsAll,
              reviews: stageReviewCardsAll,
              referrals: stageReferralsAll,
            })
          : {
              files: stageFilesAll,
              preps: stagePrepsAll,
              reviews: stageReviewCardsAll,
              referrals: stageReferralsAll,
            };
        // ONE derived collection of everything this column actually
        // renders — file folders + prep sheets + review calendar cards +
        // referral cards.
        // BOTH the header badge and the empty-state placeholder are driven
        // from this. Previously both counted files only, so a column
        // holding just prep sheets or just calendar cards believed it was
        // empty: it printed the placeholder ABOVE those cards and pushed
        // them down the column, out of line with its neighbours.
        //
        // DISPLAY COUNT ONLY (CLAUDE.md §21.1): this is a count of cards
        // visible in a column, not a statistic. It is never routed into
        // file statistics, prep statistics, the bonus counter, SLA
        // calculations, or the file concurrency gate — those are computed
        // elsewhere, each from its own table.
        const stageCardCount =
          stageFiles.length + stagePreps.length + stageReviewCards.length + stageReferrals.length;
        // In Progress shows the "no active file" banner INSTEAD of the
        // placeholder when the slot is empty with work waiting (§7.6).
        const showsInProgressBanner =
          stage.key === 'in_progress' && shouldShowNoActiveBanner(files);
        const Icon = stage.icon;
        return (
          <div key={stage.key} style={styles.column}>
            <div style={styles.columnHeader}>
              <Icon size={14} color={stage.color} />
              <span style={styles.columnLabel}>{stage.label}</span>
              <span style={{ ...styles.columnCount, background: `${stage.color}18`, color: stage.color }}>
                {/* Every card visible in this column — files, prep
                    sheets, review calendar cards (§34.9) and referral
                    cards. The badge
                    means the same thing for every role (§27.2), and is
                    presentational only (see stageCardCount above). */}
                {stageCardCount}
              </span>
            </div>
            <div style={styles.columnCards}>
              {stage.key === 'completed' ? (
                /* File Completed: most-recently-completed file as a standalone
                   FolderCard; older completions collapse into the FannedStack.
                   Acknowledgement illumination (CLAUDE.md §31) is intentionally
                   NOT applied here — this column is the archive, its cards
                   suppress the note badge for completion-time, and the fanned
                   navy tabs have no amber affordance. The per-note checkbox is
                   still available on the file's detail view. */
                stageFiles.length > 0 && (
                  <>
                    <FolderCard
                      key={stageFiles[0].id}
                      file={stageFiles[0]}
                      notesCount={noteCounts[stageFiles[0].id] || 0}
                      completedView
                      interactive={canOpenFiles}
                      onClick={() => handleFileOpen(stageFiles[0])}
                    />
                    {stageFiles.length > 1 && (
                      <div style={{ marginTop: '14px' }}>
                        <FannedStack
                          files={stageFiles.slice(1)}
                          notesCountFor={(f) => noteCounts[f.id] || 0}
                          onFileClick={handleFileOpen}
                          interactive={canOpenFiles}
                          completedView
                        />
                      </div>
                    )}
                  </>
                )
              ) : (
                <>
                  {stage.key === 'in_progress' && shouldShowNoActiveBanner(files) && (
                    <InProgressEmptyWarning />
                  )}
                  {stageFiles.map((file) => {
                    // Inbox draw gate (CLAUDE.md §40): suppress both the note
                    // count and the acknowledgement illumination for the
                    // Administration Assistant on sealed Inbox files. The
                    // shared helper now expresses what the dashboard-supplied
                    // suppressSubmittedNotes prop used to (that prop was only
                    // ever passed by the Administration Assistant dashboard).
                    const suppressed = isSealedInInbox('file', file.status, role);
                    return (
                      <FolderCard
                        key={file.id}
                        file={file}
                        notesCount={suppressed ? 0 : noteCounts[file.id] || 0}
                        pendingAck={suppressed ? 0 : myPending(file.id)}
                        hasOpenFlag={file.status === 'signed' && (flaggedCounts[file.id] || 0) > 0}
                        interactive={canOpenFiles}
                        onClick={() => handleFileOpen(file)}
                      />
                    );
                  })}
                </>
              )}

              {/* Preparation requests — their own clearly separated group,
                  only in Inbox / In Progress / Completed. Acknowledgement
                  illumination is suppressed in the Completed column, mirroring
                  the file-card rule (§31.4). */}
              {stage.key === 'completed' ? (
                /* Completed preps stack like completed files (§20.8): the
                   most recently completed sheet as a standalone full card,
                   older completions collapsed into the notebook-styled
                   PrepFannedStack. The prep stack is SEPARATE from the file
                   stack above — a prep never enters the folder stack or
                   vice versa — and the preps keep their existing
                   completed-desc order and their position beneath the file
                   block. */
                stagePreps.length > 0 && (
                  <>
                    <PrepSheetCard
                      key={stagePreps[0].id}
                      prep={stagePreps[0]}
                      interactive={canOpenPrep(stagePreps[0])}
                      notesCount={prepNoteCounts[stagePreps[0].id] || 0}
                      onClick={() => handlePrepOpen(stagePreps[0])}
                    />
                    {stagePreps.length > 1 && (
                      <div style={{ marginTop: '14px' }}>
                        <PrepFannedStack
                          preps={stagePreps.slice(1)}
                          notesCountFor={(p) => prepNoteCounts[p.id] || 0}
                          onPrepClick={handlePrepOpen}
                          interactive={canOpenPrep}
                        />
                      </div>
                    )}
                  </>
                )
              ) : (
                <PrepColumnGroup
                  preps={stagePreps}
                  onOpen={handlePrepOpen}
                  interactive={canOpenPrep}
                  noteCounts={prepNoteCounts}
                  myPendingFor={myPrepPending}
                  // Inbox draw gate (§40): a sealed Inbox prep request shows
                  // no note badge and no illumination to the New Business
                  // Assistant — the same suppression sealed files get above.
                  sealedFor={(p) => isSealedInInbox('prep', p.status, role)}
                  illuminate
                />
              )}

              {/* Review calendar cards (§34.9/§34.10) — flow beneath the
                  other card types, visible to every role. Only the
                  Adviser gets the openable affordance; the column count
                  badge above counts them alongside the other card
                  kinds. */}
              {stageReviewCards.map((rcard) => (
                <CalendarCard
                  key={rcard.key}
                  card={rcard}
                  interactive={canOpenReviews}
                  onClick={() => canOpenReviews && onReviewClick && onReviewClick(rcard)}
                />
              ))}

              {/* Referral cards — the fourth card type, flowing beneath
                  the others in Inbox / In Progress / Paused / Completed.
                  Visible to every role; only the Adviser gets the
                  openable affordance. Display only in this step — no
                  detail view is wired yet, so the click is a no-op until
                  onReferralClick is supplied. Completed referrals render
                  as a flat month-filtered list (no fanned stack). */}
              {stageReferrals.map((referral) => (
                <ReferralCard
                  key={referral.id}
                  referral={referral}
                  interactive={canOpenReferrals && !!onReferralClick}
                  onClick={() => canOpenReferrals && onReferralClick && onReferralClick(referral)}
                />
              ))}

              {/* The column's ONE empty state — renders only when the
                  column genuinely has nothing to show, of any card kind.
                  Wording is neutral because a column can hold file
                  folders, prep sheets, review calendar cards and
                  referral cards. */}
              {stageCardCount === 0 && !showsInProgressBanner && (
                <p style={styles.emptyColumn}>Nothing here yet</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Prep-sheet cards for a column. On the Adviser board they flow directly
 * beneath the file cards; on the New Business Assistant board they are the
 * only cards. No group header, count badge, or container box — the notebook
 * card design alone keeps them visually distinct. Renders nothing when there
 * are no prep requests for that column.
 */
function PrepColumnGroup({
  preps,
  onOpen,
  // Boolean OR a per-record predicate `(prep) => boolean` — prep access
  // is per record (§26.1), so one column can mix openable and inert
  // sheets for the Administration Assistant.
  interactive = true,
  noteCounts = {},
  myPendingFor = () => 0,
  // Per-record predicate: true when the sheet is sealed for the viewer
  // (Inbox draw gate, §40) — its note badge and illumination are then
  // suppressed, mirroring the file-card rule.
  sealedFor = () => false,
  illuminate = true,
}) {
  if (!preps || preps.length === 0) return null;
  const interactiveFor = (prep) =>
    typeof interactive === 'function' ? !!interactive(prep) : interactive !== false;
  return (
    <>
      {preps.map((p) => {
        const sealed = sealedFor(p);
        return (
          <PrepSheetCard
            key={p.id}
            prep={p}
            interactive={interactiveFor(p)}
            notesCount={sealed ? 0 : noteCounts[p.id] || 0}
            pendingAck={illuminate && !sealed ? myPendingFor(p.id) : 0}
            onClick={() => onOpen && onOpen(p)}
          />
        );
      })}
    </>
  );
}

const styles = {
  pipeline: {
    display: 'flex',
    gap: '0.75rem',
    overflowX: 'auto',
    paddingBottom: '1rem',
    alignItems: 'flex-start',
  },
  column: {
    minWidth: '180px',
    flex: '1 1 180px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    verticalAlign: 'top',
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginBottom: '0.75rem',
    padding: '0.5rem 0',
    height: '48px',
    minHeight: '48px',
  },
  columnLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    flex: 1,
  },
  columnCount: {
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '0.1rem 0.4rem',
    borderRadius: '999px',
  },
  columnCards: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: '0.5rem',
  },
  emptyColumn: {
    fontSize: '0.8rem',
    color: '#6B7A8D',
    textAlign: 'center',
    padding: '2rem 0',
    fontStyle: 'italic',
  },
};
